import React, { useState, useEffect, useCallback } from 'react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import AddCashModal from '../../Elements/AddCashModal';

// Formatea el `detail` de un error FastAPI (string | array de validación | objeto)
// a un mensaje legible — evita el "[object Object]".
function formatApiError(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join(' · ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

export default function PaymentModal({
  active,
  onClose,
  onPaymentComplete,
  items = [],
  total = 0,
  subtotal,
  discountAmount,
  discountLabel,
  clientName,
  clientEmail,
  userId,
  venueId,
  cartType,
  allCartTypes,
  token,
}) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState(null);     // saldo actual del socio (null = desconocido)
  const [showAddCash, setShowAddCash] = useState(false);

  const displayTotal = total ?? 0;

  const refreshBalance = useCallback(async () => {
    if (!userId || !venueId || !token) return;
    const q = (clientName || clientEmail || '').trim();
    if (!q) return;
    try {
      const res = await fetch(
        `/v1/clients/search/${venueId}?q=${encodeURIComponent(q)}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const list = await res.json();
      const me = Array.isArray(list) ? list.find((c) => String(c.id) === String(userId)) : null;
      const bal = me?.profile?.credit_balance ?? me?.credit_balance;
      if (bal != null) setBalance(Number(bal));
    } catch { /* silent */ }
  }, [userId, venueId, token, clientName, clientEmail]);

  useEffect(() => {
    if (active) {
      setProcessing(false);
      setError('');
      setBalance(null);
      setShowAddCash(false);
      refreshBalance();
    }
  }, [active, refreshBalance]);

  // Cierre por fondo que sobrevive a la selección de texto (ver useOverlayDismiss).
  const overlayDismiss = useOverlayDismiss(onClose);

  if (!active) return null;

  const fmtEur = (v) =>
    Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const hasSocio = !!userId;
  const afterBalance = balance != null ? balance - displayTotal : null;
  const insufficient = afterBalance != null && afterBalance < 0;

  const handleCreditPayment = async () => {
    if (!hasSocio) {
      setError('Este carrito no tiene socio asignado. Asigna un socio para poder pagar con saldo.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const res = await fetch('/v1/make-purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          venue_id: venueId,
          items: items.map((i) => ({
            product_id: i.product_id || i.id,
            quantity: i.quantity,
            unit_price: i.unit_price || i.price,
            real_weight: i.real_weight != null ? i.real_weight : undefined,
            location_id: i.location_id != null ? i.location_id : undefined,  // ubicación elegida (Fase D)
            discount_pct: i.manual_discount != null ? i.manual_discount : undefined,  // descuento manual por línea (%)
          })),
          payment_method: 'credit',
          cart_type: cartType || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err.detail, 'Error al procesar pago con saldo'));
      }
      const data = await res.json();
      onPaymentComplete({
        method: 'credit',
        transactionId: data.transaction_id,
        newBalance: data.new_balance != null ? Number(data.new_balance) : undefined,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      {...overlayDismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
       
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-purple-900 rounded-t-2xl px-6 py-4 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Pagar con Saldo</h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
          {clientName && (
            <p className="text-purple-200 text-sm mt-1">
              Cliente: <span className="text-white font-medium">{clientName}</span>
              {clientEmail && <span className="ml-2 text-purple-300">({clientEmail})</span>}
            </p>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* Order summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            {allCartTypes && allCartTypes.length > 1 && (() => {
              const grouped = {};
              for (const it of items) {
                const ct = it._cartType || 'otro';
                if (!grouped[ct]) grouped[ct] = [];
                grouped[ct].push(it);
              }
              return Object.entries(grouped).map(([ct, groupItems]) => {
                const groupTotal = groupItems.reduce(
                  (s, i) => s + (i.quantity || 1) * parseFloat(i.unit_price || i.price || 0), 0
                );
                const label = ct === 'productos' ? 'Productos' : ct === 'dispensario' ? 'Dispensario' : ct;
                const color = ct === 'productos' ? 'text-blue-700' : 'text-green-700';
                const bgColor = ct === 'productos' ? 'bg-blue-50' : 'bg-green-50';
                return (
                  <div key={ct} className={`${bgColor} rounded-lg p-2.5`}>
                    <div className={`flex justify-between text-sm font-semibold ${color}`}>
                      <span>{label}</span>
                      <span>€{fmtEur(groupTotal)}</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {groupItems.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-xs text-gray-600 pl-2">
                          <span>{it.product_name || `Producto #${(it.product_id || it.id || '').toString().slice(0, 8)}`} ×{it.quantity || 1}</span>
                          <span>€{fmtEur((it.quantity || 1) * parseFloat(it.unit_price || it.price || 0))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}

            {subtotal != null && discountAmount != null && discountAmount > 0 ? (
              <>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>€{fmtEur(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento {discountLabel && `(${discountLabel})`}</span>
                  <span>-€{fmtEur(discountAmount)}</span>
                </div>
              </>
            ) : null}
            <div className={`flex justify-between text-lg font-bold text-gray-900 pt-1 ${allCartTypes && allCartTypes.length > 1 ? 'border-t-2 border-gray-300 mt-1' : 'border-t border-gray-200'}`}>
              <span>Total</span>
              <span>€{fmtEur(displayTotal)}</span>
            </div>
            {cartType && !allCartTypes && (
              <div className="text-xs text-gray-500 pt-1">
                Tipo: <span className="capitalize font-medium">{cartType}</span>
              </div>
            )}
            {allCartTypes && allCartTypes.length > 1 && (
              <div className="text-xs text-gray-500 pt-1">
                Tipo: <span className="font-medium">Dispensario + Productos (Todo)</span>
              </div>
            )}
          </div>

          {/* Saldo del socio */}
          {hasSocio && (
            <div className="rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Saldo del socio</div>
                {balance != null ? (
                  <>
                    <div className="text-lg font-bold text-gray-900">€{fmtEur(balance)}</div>
                    <div className={`text-xs ${insufficient ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      Tras el pago: €{fmtEur(afterBalance)}{insufficient ? ' · saldo insuficiente' : ''}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400">—</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowAddCash(true)}
                className="flex-shrink-0 h-9 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 px-3 text-sm font-semibold transition-colors"
              >
                Añadir saldo
              </button>
            </div>
          )}

          {/* Saldo payment */}
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
              <p className="text-sm text-purple-600">Se descontará del saldo del socio</p>
              <p className="text-2xl font-bold text-purple-800 mt-1">€{fmtEur(displayTotal)}</p>
            </div>
            {!hasSocio && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 text-center">
                Este carrito no tiene socio asignado.
              </div>
            )}
            <button
              onClick={handleCreditPayment}
              disabled={processing || !hasSocio}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              {processing ? 'Procesando...' : 'Confirmar Pago con Saldo'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Recarga de saldo (reutiliza AddCashModal) */}
      {showAddCash && (
        <AddCashModal
          active={showAddCash}
          handleAddCashModal={() => setShowAddCash(false)}
          token={token}
          user_id={userId}
          venueID={venueId}
          currentBalance={balance ?? 0}
          memberName={clientName}
          onCashAdded={(id, newBal) => {
            if (newBal != null) setBalance(Number(newBal));
            else refreshBalance();
            setShowAddCash(false);
          }}
        />
      )}
    </div>
  );
}
